import React, { useEffect, useState } from 'react';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import { storageBoxApi } from '../api/storageBoxApi';
import CopyShareLinkButton from './CopyShareLinkButton';
import { buildStorageBoxShareLinkForShare, rememberShareLinkToken } from '../utils/shareLinkUrl';

function cacheShareTokens(shares) {
  (shares || []).forEach((s) => {
    if (s.kind === 'link' && s.linkToken && s.shareId) rememberShareLinkToken(s.shareId, s.linkToken);
  });
}

export default function ShareModal({ item, onClose, onNotify }) {
  const [mode, setMode] = useState('user');
  const [neverExpires, setNeverExpires] = useState(false);
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item?.itemId) return;
    storageBoxApi
      .listShares(item.itemId)
      .then((r) => {
        const list = r.shares || [];
        cacheShareTokens(list);
        setShares(list);
      })
      .catch(() => {});
  }, [item?.itemId]);

  useEffect(() => {
    const t = query.trim();
    if (t.length < 2) {
      setUsers([]);
      return;
    }
    const handle = setTimeout(() => {
      storageBoxApi.searchUsers(t).then((r) => setUsers(r.users || [])).catch(() => setUsers([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const refreshShares = () =>
    storageBoxApi
      .listShares(item.itemId)
      .then((r) => {
        const list = r.shares || [];
        cacheShareTokens(list);
        setShares(list);
      })
      .catch(() => {});

  const handleCreate = async () => {
    setBusy(true);
    try {
      const body = {
        itemId: item.itemId,
        kind: mode,
        neverExpires,
        expiresInHours,
        granteeUserId: mode === 'user' ? selectedUser?.userId : undefined,
      };
      const res = await storageBoxApi.createShare(body);
      if (res.linkToken) {
        const sid = res.share?.shareId;
        if (sid) rememberShareLinkToken(sid, res.linkToken);
        onNotify?.(res.reused ? 'Using your existing share link' : 'Link created');
      } else {
        onNotify?.('Shared with user');
        setSelectedUser(null);
        setQuery('');
      }
      await refreshShares();
    } catch (e) {
      onNotify?.(e.message || 'Share failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (shareId) => {
    try {
      await storageBoxApi.revokeShare(shareId);
      await refreshShares();
      onNotify?.('Share revoked');
    } catch (e) {
      onNotify?.(e.message || 'Failed');
    }
  };

  const existingUserIds = new Set(shares.filter((s) => s.kind === 'user').map((s) => s.granteeUserId));

  return (
    <CenterModalWindow
      isOpen={Boolean(item)}
      onClose={onClose}
      width="min(520px, calc(100vw - 32px))"
      height="auto"
      contentStyle={{ height: 'auto', maxHeight: 'min(85vh, 720px)' }}
      contentClassName="sb-share-modal"
    >
      <div className="sb-share-modal__inner" data-modal-scroll-port>
        <header className="sb-share-modal__head">
          <h2 id="sb-share-title">Share “{item?.name}”</h2>
        </header>
        <div className="sb-share-modal__body">
          <div className="sb-share-tabs">
            <button
              type="button"
              className={mode === 'user' ? 'sb-share-tabs__btn sb-share-tabs__btn--active' : 'sb-share-tabs__btn'}
              onClick={() => setMode('user')}
            >
              People
            </button>
            <button
              type="button"
              className={mode === 'link' ? 'sb-share-tabs__btn sb-share-tabs__btn--active' : 'sb-share-tabs__btn'}
              onClick={() => setMode('link')}
            >
              Link
            </button>
          </div>

          <label className="sb-field sb-field--checkbox">
            <input type="checkbox" checked={neverExpires} onChange={(e) => setNeverExpires(e.target.checked)} />
            Never expires
          </label>
          {!neverExpires ? (
            <label className="sb-field">
              <span>Expires in (hours)</span>
              <input
                type="number"
                min={1}
                max={720}
                value={expiresInHours}
                onChange={(e) => setExpiresInHours(Number(e.target.value) || 24)}
              />
            </label>
          ) : null}

          {mode === 'user' ? (
            <>
              <label className="sb-field">
                <span>Add people</span>
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name or email" />
              </label>
              <ul className="sb-user-pick-list">
                {users.map((u) => {
                  const already = existingUserIds.has(u.userId);
                  return (
                    <li key={u.userId}>
                      <button
                        type="button"
                        disabled={already}
                        className={selectedUser?.userId === u.userId ? 'sb-user-pick sb-user-pick--active' : 'sb-user-pick'}
                        onClick={() => setSelectedUser(u)}
                      >
                        <span>{u.displayName}</span>
                        <small>{already ? 'Already shared' : u.email}</small>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="sb-hint">Anyone with the link can view this item until the link expires.</p>
          )}

          <button
            type="button"
            className="sb-primary-btn"
            disabled={busy || (mode === 'user' && !selectedUser)}
            onClick={handleCreate}
          >
            {busy ? 'Saving…' : mode === 'user' ? 'Add person' : 'Create link'}
          </button>

          {shares.length ? (
            <div className="sb-share-list">
              <h3>Who has access</h3>
              <ul>
                {shares.map((s) => {
                  const link = buildStorageBoxShareLinkForShare(s);
                  return (
                    <li key={s.shareId}>
                      <div className="sb-share-list__row">
                        <span>
                          {s.kind === 'user'
                            ? `${s.granteeName || s.granteeUserId}${s.granteeEmail ? ` · ${s.granteeEmail}` : ''}`
                            : s.linkLabel || 'Anyone with the link'}
                          {s.expiresAt ? ` · until ${new Date(s.expiresAt).toLocaleString()}` : ' · no expiry'}
                        </span>
                        {s.kind === 'user' ? (
                          <button type="button" className="sb-revoke-share-btn" onClick={() => handleRevoke(s.shareId)}>
                            Revoke
                          </button>
                        ) : null}
                      </div>
                      {link ? (
                        <div className="sb-link-box sb-link-box--compact">
                          <input readOnly value={link} aria-label="Share link" onFocus={(e) => e.target.select()} />
                          <div className="sb-link-box__actions">
                            <CopyShareLinkButton text={link} onFailed={() => onNotify?.('Could not copy link')} />
                            <button type="button" className="sb-revoke-share-btn" onClick={() => handleRevoke(s.shareId)}>
                              Revoke
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
        <footer className="sb-share-modal__foot">
          <button type="button" className="sb-secondary-btn" onClick={onClose}>Done</button>
        </footer>
      </div>
    </CenterModalWindow>
  );
}
